import type { Place, PlaceWithDistance } from './types';
import { HOME } from './home';
import { estimateDriveMinutes, haversineKm } from './geo';
import { imageForPlace } from './placeImages';

/** Seeded Carvoeiro / Algarve places. Distances are computed at serve time from HOME. */
export const PLACES: Place[] = [
  {
    id: 'rocha-brava',
    slug: 'rocha-brava-village-resort',
    name: 'Rocha Brava Village Resort',
    category: 'resort',
    locality: 'inside',
    address: 'Urbanização Rocha Brava, Alfanzina, 8400-568 Carvoeiro',
    lat: 37.0897,
    lng: -8.4494,
    phone: '+351 282 350 370',
    website: 'https://www.rochabrava.com',
    notes: 'Home pin. Reception, pools, tennis, and the village square.',
    tags: ['home', 'resort', 'pool', 'tennis'],
    hoursNote: 'Reception daily',
  },
  {
    id: 'praia-carvoeiro',
    slug: 'praia-do-carvoeiro',
    name: 'Praia do Carvoeiro',
    category: 'beach',
    locality: 'inside',
    address: 'Largo da Praia, 8400-517 Carvoeiro',
    lat: 37.0969,
    lng: -8.4716,
    notes: 'Village beach under the square. Crowded in August; go early.',
    tags: ['beach', 'village', 'swim'],
  },
  {
    id: 'algar-seco',
    slug: 'algar-seco-boardwalk',
    name: 'Algar Seco & boardwalk',
    category: 'attraction',
    locality: 'inside',
    address: 'Estrada do Farol, 8400-505 Carvoeiro',
    lat: 37.0936,
    lng: -8.4678,
    notes: 'Cliff walk from the village toward Vale Centeanes. Boneca Bar at the caves.',
    tags: ['walk', 'cliffs', 'sunset'],
  },
  {
    id: 'vale-centeanes',
    slug: 'praia-vale-centeanes',
    name: 'Praia de Vale Centeanes',
    category: 'beach',
    locality: 'inside',
    address: 'Praia de Vale Centeanes, 8400-505 Carvoeiro',
    lat: 37.0902,
    lng: -8.4568,
    notes: 'Closest proper beach walk from Rocha Brava. Café and parking above.',
    tags: ['beach', 'walk'],
  },
  {
    id: 'vale-covo',
    slug: 'praia-vale-covo',
    name: 'Praia de Vale Covo',
    category: 'beach',
    locality: 'inside',
    address: 'Vale Covo, Rocha Brava, 8400-568 Carvoeiro',
    lat: 37.0878,
    lng: -8.4472,
    notes: 'Small cove below the resort. Steep access; check swell.',
    tags: ['beach', 'resort'],
  },
  {
    id: 'o-castelo',
    slug: 'o-castelo-carvoeiro',
    name: 'O Castelo',
    category: 'food',
    locality: 'inside',
    address: 'Rua do Barranco 99, 8400-508 Carvoeiro',
    lat: 37.0964,
    lng: -8.4728,
    phone: '+351 282 358 418',
    notes: 'Clifftop terrace over the village beach. Book sunset.',
    tags: ['restaurant', 'sunset', 'fish'],
    hoursNote: 'Dinner; closed some Mondays',
  },
  {
    id: 'oasis',
    slug: 'oasis-bar-carvoeiro',
    name: 'Oasis Bar',
    category: 'food',
    locality: 'inside',
    address: 'Largo da Praia, 8400-517 Carvoeiro',
    lat: 37.0971,
    lng: -8.4712,
    notes: 'Square-side drinks and snacks facing the beach.',
    tags: ['cafe', 'drinks', 'square'],
  },
  {
    id: 'bon-bon',
    slug: 'bon-bon-carvoeiro',
    name: 'Bon Bon',
    category: 'food',
    locality: 'inside',
    address: 'Urb. Cabeço de Pias, 8400-505 Carvoeiro',
    lat: 37.1024,
    lng: -8.4642,
    phone: '+351 282 341 115',
    website: 'https://www.bonbon.pt',
    notes: 'Michelin-starred tasting menu. Reserve well ahead.',
    tags: ['restaurant', 'fine dining'],
  },
  {
    id: 'farmacia-carvoeiro',
    slug: 'farmacia-carvoeiro',
    name: 'Farmácia Carvoeiro',
    category: 'medical',
    locality: 'inside',
    address: 'Estrada do Farol 15, 8400-505 Carvoeiro',
    lat: 37.0978,
    lng: -8.4694,
    phone: '+351 282 357 257',
    notes: 'Village pharmacy. Night rota is posted on the door.',
    tags: ['pharmacy', 'medical'],
    hoursNote: 'Weekdays; Saturday morning',
  },
  {
    id: 'minimercado-rb',
    slug: 'minimercado-rocha-brava',
    name: 'Minimercado Rocha Brava',
    category: 'grocery',
    locality: 'inside',
    address: 'Urbanização Rocha Brava, 8400-568 Carvoeiro',
    lat: 37.0904,
    lng: -8.4498,
    notes: 'Small shop on the resort for milk, bread, and wine.',
    tags: ['grocery', 'resort'],
  },
  {
    id: 'rei-das-praias',
    slug: 'rei-das-praias',
    name: 'Rei das Praias',
    category: 'food',
    locality: 'outside',
    address: 'Praia dos Caneiros, 8400-201 Ferragudo',
    lat: 37.1088,
    lng: -8.5146,
    phone: '+351 282 461 006',
    // OSM Rei das Praias (Caneiros), same phone, checked 22 Oct 2026.
    openingHours: 'Mo-Su 09:00-23:00',
    notes: 'Beach restaurant west of Carvoeiro. Book lunch in season.',
    tags: ['restaurant', 'beach', 'fish'],
  },
  {
    id: 'benagil',
    slug: 'praia-de-benagil',
    name: 'Praia de Benagil',
    category: 'beach',
    locality: 'outside',
    address: 'Praia de Benagil, 8400-405 Lagoa',
    lat: 37.0873,
    lng: -8.4256,
    notes: 'Cave boats leave from the beach. Park fills by 10:00 in summer.',
    tags: ['beach', 'cave', 'boats'],
  },
  {
    id: 'marinha',
    slug: 'praia-da-marinha',
    name: 'Praia da Marinha',
    category: 'beach',
    locality: 'outside',
    address: 'Praia da Marinha, 8400-405 Lagoa',
    lat: 37.09,
    lng: -8.4118,
    notes: 'Postcard cliffs. Long stairs; little shade.',
    tags: ['beach', 'cliffs', 'photo'],
  },
  {
    id: 'intermarche-lagoa',
    slug: 'intermarche-lagoa',
    name: 'Intermarché Lagoa',
    category: 'grocery',
    locality: 'outside',
    address: 'EN125, 8400-310 Lagoa',
    lat: 37.1368,
    lng: -8.4548,
    notes: 'Full supermarket ~15 minutes inland. Fuel on site.',
    tags: ['grocery', 'supermarket'],
    hoursNote: 'Daily, long hours',
  },
  {
    id: 'pingo-doce-lagoa',
    slug: 'pingo-doce-lagoa',
    name: 'Pingo Doce Lagoa',
    category: 'grocery',
    locality: 'outside',
    address: 'Rua do Mercado, 8400-310 Lagoa',
    lat: 37.1352,
    lng: -8.4536,
    // OSM Pingo Doce supermarket in Lagoa (Rua Marquês de Pombal), checked 22 Oct 2026.
    openingHours: 'Mo-Su 08:00-21:00; easter 08:00-14:00',
    notes: 'Portuguese chain; good bakery and house wine.',
    tags: ['grocery', 'supermarket'],
  },
  {
    id: 'mercado-lagoa',
    slug: 'mercado-municipal-lagoa',
    name: 'Mercado Municipal de Lagoa',
    category: 'grocery',
    locality: 'outside',
    address: 'Rua do Mercado, 8400-310 Lagoa',
    lat: 37.1356,
    lng: -8.4532,
    notes: 'Fish, fruit, and flowers. Best Saturday morning.',
    tags: ['market', 'fish', 'produce'],
    hoursNote: 'Mornings; Saturday is the big day',
  },
  {
    id: 'slide-splash',
    slug: 'slide-and-splash',
    name: 'Slide & Splash',
    category: 'recreation',
    locality: 'outside',
    address: 'Estrada Nacional 125, 8400-627 Estômbar',
    lat: 37.1286,
    lng: -8.4682,
    phone: '+351 282 340 800',
    website: 'https://www.slidesplash.com',
    notes: 'Water park toward Lagoa. Buy tickets online in July–August.',
    tags: ['kids', 'water park'],
  },
  {
    id: 'hospital-portimao',
    slug: 'hospital-portimao',
    name: 'Hospital de Portimão',
    category: 'medical',
    locality: 'outside',
    address: 'Sítio do Poço Seco, 8500-314 Portimão',
    lat: 37.1482,
    lng: -8.5484,
    phone: '+351 282 450 300',
    // OSM hospital relation, same phone, Estrada do Poço Seco, checked 22 Oct 2026.
    openingHours: '24/7',
    notes: 'CHUA emergency department for the western Algarve.',
    tags: ['hospital', 'er', 'emergency'],
  },
  {
    id: 'fao',
    slug: 'faro-airport',
    name: 'Faro Airport (FAO)',
    category: 'travel',
    locality: 'outside',
    address: 'Aeroporto de Faro, 8001-701 Faro',
    lat: 37.0144,
    lng: -7.9658,
    phone: '+351 289 800 800',
    website: 'https://www.ana.pt',
    notes: 'About 55–70 minutes east. LIS (not FAO) is the default long-haul.',
    tags: ['airport', 'travel'],
  },
  {
    id: 'boneca-bar',
    slug: 'boneca-bar',
    name: 'Boneca Bar',
    category: 'food',
    locality: 'inside',
    address: 'Algar Seco, Estrada do Farol, 8400-505 Carvoeiro',
    lat: 37.0932,
    lng: -8.4684,
    phone: '+351 282 357 390',
    notes: 'Cave terrace at Algar Seco. Sunset drinks; book in August.',
    tags: ['bar', 'caves', 'sunset'],
  },
  {
    id: 'gelataria-veneza',
    slug: 'gelataria-veneza',
    name: 'Gelataria Veneza',
    category: 'food',
    locality: 'inside',
    address: 'Rua do Barranco 35, 8400-508 Carvoeiro',
    lat: 37.0972,
    lng: -8.471,
    notes: 'Village gelato on the walk down to the beach.',
    tags: ['ice cream', 'cafe'],
  },
  {
    id: 'le-cro',
    slug: 'le-cro-carvoeiro',
    name: 'Le Crô',
    category: 'food',
    locality: 'inside',
    address: 'Rua do Barranco 58, 8400-508 Carvoeiro',
    lat: 37.0968,
    lng: -8.4718,
    phone: '+351 282 357 247',
    notes: 'French-leaning bistro a block above the square.',
    tags: ['restaurant', 'dinner'],
  },
  {
    id: 'a-lanterna',
    slug: 'a-lanterna-carvoeiro',
    name: 'A Lanterna',
    category: 'food',
    locality: 'inside',
    address: 'Estrada do Farol, 8400-505 Carvoeiro',
    lat: 37.0954,
    lng: -8.4696,
    phone: '+351 282 358 668',
    notes: 'Cataplana and grilled fish. Family-run, book weekends.',
    tags: ['restaurant', 'fish', 'cataplana'],
  },
  {
    id: 'la-piazza',
    slug: 'la-piazza-carvoeiro',
    name: 'Pizzaria La Piazza',
    category: 'food',
    locality: 'inside',
    address: 'Estrada do Farol 13, 8400-505 Carvoeiro',
    lat: 37.0976,
    lng: -8.4698,
    phone: '+351 282 357 318',
    notes: 'Reliable pizza and pasta when the kids are done with fish.',
    tags: ['pizza', 'family'],
  },
  {
    id: 'o-capelo',
    slug: 'o-capelo',
    name: 'O Capelo',
    category: 'food',
    locality: 'inside',
    address: 'Largo da Praia 7, 8400-517 Carvoeiro',
    lat: 37.0968,
    lng: -8.4714,
    phone: '+351 282 357 247',
    notes: 'Beach-square seafood. Request a terrace table.',
    tags: ['restaurant', 'beach', 'fish'],
  },
  {
    id: 'tivoli-carvoeiro',
    slug: 'tivoli-carvoeiro',
    name: 'Tivoli Carvoeiro',
    category: 'resort',
    locality: 'inside',
    address: 'Vale Covo, Apartado 1299, 8401-911 Carvoeiro',
    lat: 37.0886,
    lng: -8.4518,
    phone: '+351 282 351 100',
    website: 'https://www.tivolihotels.com',
    notes: 'Cliff hotel next door to Rocha Brava. Spa and Vale Covo lift.',
    tags: ['hotel', 'spa', 'cliffs'],
  },
  {
    id: 'praia-paraiso',
    slug: 'praia-do-paraiso',
    name: 'Praia do Paraíso',
    category: 'beach',
    locality: 'inside',
    address: 'Praia do Paraíso, 8400-517 Carvoeiro',
    lat: 37.0986,
    lng: -8.4748,
    notes: 'Tiny cove west of the village. Steep stairs; check tide.',
    tags: ['beach', 'cove'],
  },
  {
    id: 'praia-carvalho',
    slug: 'praia-do-carvalho',
    name: 'Praia do Carvalho',
    category: 'beach',
    locality: 'inside',
    address: 'Praia do Carvalho, 8400-405 Carvoeiro',
    lat: 37.0884,
    lng: -8.4342,
    notes: 'Tunnel beach toward Benagil. Park above and walk down.',
    tags: ['beach', 'tunnel'],
  },
  {
    id: 'vale-de-milho-golf',
    slug: 'vale-de-milho-golf',
    name: 'Vale de Milho Golf',
    category: 'recreation',
    locality: 'inside',
    address: 'Vale de Milho, 8400-555 Carvoeiro',
    lat: 37.1028,
    lng: -8.4486,
    phone: '+351 282 358 502',
    website: 'https://www.valedemilhogolf.com',
    notes: '9-hole par-3 a few minutes inland from Rocha Brava.',
    tags: ['golf', 'short course'],
  },
  {
    id: 'miniproco-carvoeiro',
    slug: 'minipreco-carvoeiro',
    name: 'Minipreço Carvoeiro',
    category: 'grocery',
    locality: 'inside',
    address: 'Estrada de Vale Centeanes, 8400-505 Carvoeiro',
    lat: 37.0994,
    lng: -8.4612,
    notes: 'Closest proper grocery if you do not want to drive to Lagoa.',
    tags: ['grocery', 'supermarket'],
  },
  {
    id: 'farmacia-rocha-brava',
    slug: 'farmacia-rocha-brava',
    name: 'Farmácia Rocha Brava',
    category: 'medical',
    locality: 'inside',
    address: 'Urbanização Rocha Brava, 8400-568 Carvoeiro',
    lat: 37.0912,
    lng: -8.4506,
    phone: '+351 282 357 174',
    notes: 'Small pharmacy by the resort entrance. Confirm summer hours.',
    tags: ['pharmacy', 'medical'],
  },
  {
    id: 'carvoeiro-tennis',
    slug: 'carvoeiro-clube-tenis',
    name: 'Carvoeiro Clube de Ténis',
    category: 'recreation',
    locality: 'inside',
    address: 'Mato Serrão, 8400-525 Carvoeiro',
    lat: 37.1046,
    lng: -8.4688,
    phone: '+351 282 357 847',
    notes: 'Courts and padel above the village. Book ahead in season.',
    tags: ['tennis', 'padel'],
  },
  {
    id: 'nossa-senhora',
    slug: 'fortaleza-nossa-senhora',
    name: 'Nossa Senhora da Encarnação',
    category: 'attraction',
    locality: 'inside',
    address: 'Largo da Praia, 8400-517 Carvoeiro',
    lat: 37.0962,
    lng: -8.4724,
    notes: 'Chapel and fort above the village beach. Best village overlook.',
    tags: ['chapel', 'view', 'walk'],
  },
  {
    id: 'the-garden',
    slug: 'the-garden-carvoeiro',
    name: 'The Garden',
    category: 'food',
    locality: 'inside',
    address: 'Rua dos Pescadores 38, 8400-512 Carvoeiro',
    lat: 37.0978,
    lng: -8.4722,
    notes: 'Shaded courtyard restaurant one street back from the square.',
    tags: ['restaurant', 'garden'],
  },
  {
    id: 'pestana-palm',
    slug: 'pestana-palm-gardens',
    name: 'Pestana Palm Gardens',
    category: 'resort',
    locality: 'inside',
    address: 'Praia do Vale de Centeanes, 8400-525 Carvoeiro',
    lat: 37.0918,
    lng: -8.4554,
    phone: '+351 282 350 000',
    website: 'https://www.pestana.com',
    notes: 'Apartment resort above Vale Centeanes. Pools and cliff path.',
    tags: ['hotel', 'pool'],
  },
  {
    id: 'clinica-carvoeiro',
    slug: 'clinica-dental-carvoeiro',
    name: 'Clínica Dentária Carvoeiro',
    category: 'medical',
    locality: 'inside',
    address: 'Estrada do Farol 21, 8400-505 Carvoeiro',
    lat: 37.0982,
    lng: -8.4686,
    phone: '+351 282 357 890',
    notes: 'Village dentist. Call for English-speaking hours.',
    tags: ['dentist', 'medical'],
  },
  {
    id: 'rocha-brava-tennis',
    slug: 'rocha-brava-tennis',
    name: 'Rocha Brava tennis courts',
    category: 'recreation',
    locality: 'inside',
    address: 'Urbanização Rocha Brava, 8400-568 Carvoeiro',
    lat: 37.0901,
    lng: -8.4486,
    phone: '+351 282 350 370',
    notes: 'Book at reception. Floodlit courts on the village loop.',
    tags: ['tennis', 'resort'],
  },
  {
    id: 'gramacho-golf',
    slug: 'pestana-gramacho-golf',
    name: 'Pestana Gramacho Golf',
    category: 'recreation',
    locality: 'outside',
    address: 'Gramacho, 8400-909 Carvoeiro',
    lat: 37.1184,
    lng: -8.4622,
    phone: '+351 282 340 900',
    website: 'https://www.pestanagolf.com',
    notes: 'Championship 18 inland of Carvoeiro. Resort guests get tee times.',
    tags: ['golf', '18 hole'],
  },
  {
    id: 'vale-da-pinta',
    slug: 'pestana-vale-da-pinta',
    name: 'Pestana Vale da Pinta',
    category: 'recreation',
    locality: 'outside',
    address: 'Gramacho, 8401-908 Carvoeiro',
    lat: 37.1212,
    lng: -8.4584,
    phone: '+351 282 340 900',
    website: 'https://www.pestanagolf.com',
    notes: 'Sister course to Gramacho. Wider fairways, same clubhouse area.',
    tags: ['golf', '18 hole'],
  },
  {
    id: 'alto-golf',
    slug: 'alto-golf',
    name: 'Alto Golf',
    category: 'recreation',
    locality: 'outside',
    address: 'Quinta do Alto do Poço, 8501-928 Alvor',
    lat: 37.1416,
    lng: -8.5762,
    phone: '+351 282 460 870',
    notes: 'Sir Henry Cotton layout toward Alvor. 25 minutes in traffic.',
    tags: ['golf'],
  },
  {
    id: 'zoomarine',
    slug: 'zoomarine-algarve',
    name: 'Zoomarine',
    category: 'recreation',
    locality: 'outside',
    address: 'EN125, 8201-864 Guia',
    lat: 37.1218,
    lng: -8.3236,
    phone: '+351 289 560 300',
    website: 'https://www.zoomarine.pt',
    notes: 'Marine park toward Albufeira. Buy timed tickets online.',
    tags: ['kids', 'park'],
  },
  {
    id: 'aqualand',
    slug: 'aqualand-algarve',
    name: 'Aqualand Algarve',
    category: 'recreation',
    locality: 'outside',
    address: 'Sítio das Eiras, 8365-908 Alcantarilha',
    lat: 37.1442,
    lng: -8.3468,
    phone: '+351 282 320 230',
    website: 'https://www.aqualand.pt',
    notes: 'Water park east on the 125. Pairs with Zoomarine for a kid day.',
    tags: ['kids', 'water park'],
  },
  {
    id: 'silves-castle',
    slug: 'castelo-de-silves',
    name: 'Castelo de Silves',
    category: 'attraction',
    locality: 'outside',
    address: 'Rua do Castelo, 8300-129 Silves',
    lat: 37.1912,
    lng: -8.4386,
    notes: 'Red-sandstone castle and old town. 25 minutes inland.',
    tags: ['castle', 'day trip'],
  },
  {
    id: 'ferragudo',
    slug: 'ferragudo-village',
    name: 'Ferragudo village',
    category: 'attraction',
    locality: 'outside',
    address: 'Largo 25 de Abril, 8400-201 Ferragudo',
    lat: 37.1254,
    lng: -8.5192,
    notes: 'White village across the Arade. Park above and walk down.',
    tags: ['village', 'day trip'],
  },
  {
    id: 'praia-da-rocha',
    slug: 'praia-da-rocha',
    name: 'Praia da Rocha',
    category: 'beach',
    locality: 'outside',
    address: 'Avenida Tomás Cabreira, 8500-802 Portimão',
    lat: 37.1168,
    lng: -8.5364,
    notes: 'Long town beach and cliff promenade. Nightlife after dark.',
    tags: ['beach', 'portimao'],
  },
  {
    id: 'porches-pottery',
    slug: 'olaio-porches-pottery',
    name: 'Olaria Pequena / Porches pottery',
    category: 'shopping',
    locality: 'outside',
    address: 'EN125, 8400-450 Porches',
    lat: 37.1268,
    lng: -8.4012,
    notes: 'Roadside ceramics on the 125 toward Albufeira.',
    tags: ['pottery', 'gifts'],
  },
  {
    id: 'continente-lagoa',
    slug: 'continente-modelo-lagoa',
    name: 'Continente Modelo Lagoa',
    category: 'grocery',
    locality: 'outside',
    address: 'EN125, 8400-310 Lagoa',
    lat: 37.1376,
    lng: -8.4568,
    notes: 'Big weekly shop. Pharmacy and phones in the same plaza.',
    tags: ['grocery', 'supermarket'],
    hoursNote: 'Daily, long hours',
  },
  {
    id: 'centro-saude-lagoa',
    slug: 'centro-saude-lagoa',
    name: 'Centro de Saúde de Lagoa',
    category: 'medical',
    locality: 'outside',
    address: 'Rua da Escola, 8400-387 Lagoa',
    lat: 37.1344,
    lng: -8.4512,
    phone: '+351 282 340 260',
    notes: 'Public health centre. Hospital de Portimão for emergencies.',
    tags: ['clinic', 'sns'],
  },
  {
    id: 'clinica-sao-goncalo',
    slug: 'hospital-sao-goncalo-portimao',
    name: 'Hospital Particular São Gonçalo',
    category: 'medical',
    locality: 'outside',
    address: 'Rua 5 de Outubro, 8500-705 Portimão',
    lat: 37.1396,
    lng: -8.5418,
    phone: '+351 282 420 100',
    website: 'https://www.hppsaude.pt',
    notes: 'Private hospital in Portimão. English widely spoken.',
    tags: ['hospital', 'private'],
  },
  {
    id: 'aqua-portimao',
    slug: 'aqua-portimao',
    name: 'Aqua Portimão',
    category: 'shopping',
    locality: 'outside',
    address: 'Rua de São Pedro 8, 8500-802 Portimão',
    lat: 37.1392,
    lng: -8.5376,
    website: 'https://www.aquaportimao.pt',
    notes: 'Mall with FNAC, food court, and cinema.',
    tags: ['mall', 'shopping'],
  },
  {
    id: 'sete-vales',
    slug: 'seven-hanging-valleys',
    name: 'Seven Hanging Valleys Trail',
    category: 'attraction',
    locality: 'outside',
    address: 'Praia da Marinha trailhead, 8400-405 Lagoa',
    lat: 37.0896,
    lng: -8.4124,
    notes: 'Cliff path Marinha ↔ Vale Centeanes. Start early; take water.',
    tags: ['hike', 'cliffs', 'walk'],
  },
  {
    id: 'albandeira',
    slug: 'praia-de-albandeira',
    name: 'Praia de Albandeira',
    category: 'beach',
    locality: 'outside',
    address: 'Praia de Albandeira, 8400-405 Lagoa',
    lat: 37.0868,
    lng: -8.3994,
    notes: 'Arch beach east of Marinha. Narrow access road.',
    tags: ['beach', 'arch'],
  },
  {
    id: 'senhora-da-rocha',
    slug: 'senhora-da-rocha',
    name: 'Nossa Senhora da Rocha',
    category: 'attraction',
    locality: 'outside',
    address: 'Praia de Nossa Senhora da Rocha, 8400-398 Porches',
    lat: 37.0964,
    lng: -8.3848,
    notes: 'Chapel on a headland between two beaches. Easy parking.',
    tags: ['chapel', 'beach', 'view'],
  },
  {
    id: 'quinta-dos-vales',
    slug: 'quinta-dos-vales',
    name: 'Quinta dos Vales',
    category: 'attraction',
    locality: 'outside',
    address: 'Sítio dos Barros, 8400-031 Estômbar',
    lat: 37.1468,
    lng: -8.4864,
    phone: '+351 282 431 036',
    website: 'https://www.quintadosvales.eu',
    notes: 'Wine estate with sculpture park. Tastings by appointment.',
    tags: ['wine', 'day trip'],
  },
  {
    id: 'lagos-old-town',
    slug: 'lagos-old-town',
    name: 'Lagos old town',
    category: 'attraction',
    locality: 'outside',
    address: 'Praça Infante Dom Henrique, 8600-537 Lagos',
    lat: 37.1018,
    lng: -8.6732,
    notes: 'Walled town and marina. 35–45 minutes west.',
    tags: ['day trip', 'old town'],
  },
  {
    id: 'praia-grande-ferragudo',
    slug: 'praia-grande-ferragudo',
    name: 'Praia Grande (Ferragudo)',
    category: 'beach',
    locality: 'outside',
    address: 'Praia Grande, 8400-201 Ferragudo',
    lat: 37.1186,
    lng: -8.5214,
    notes: 'Wide river-mouth beach under the castle. Shallow for kids.',
    tags: ['beach', 'kids'],
  },
  {
    id: 'carvoeiro-caves',
    slug: 'carvoeiro-caves-boats',
    name: 'Carvoeiro Caves boat trips',
    category: 'recreation',
    locality: 'inside',
    address: 'Largo da Praia, 8400-517 Carvoeiro',
    lat: 37.097,
    lng: -8.4715,
    notes: 'Benagil cave boats from the village beach. Book the first slot.',
    tags: ['boats', 'caves', 'benagil'],
  },
  {
    id: 'algarve-shopping',
    slug: 'algarve-shopping-guia',
    name: 'Algarve Shopping',
    category: 'shopping',
    locality: 'outside',
    address: 'EN125, 8200-385 Guia',
    lat: 37.1264,
    lng: -8.2998,
    website: 'https://www.algarveshopping.pt',
    notes: 'Largest mall on the 125. Continente, cinema, IKEA nearby.',
    tags: ['mall', 'shopping'],
  },
  {
    id: 'gnr-lagoa',
    slug: 'gnr-lagoa',
    name: 'GNR Lagoa',
    category: 'medical',
    locality: 'outside',
    address: 'Rua da Portela, 8400-397 Lagoa',
    lat: 37.1362,
    lng: -8.4498,
    phone: '+351 282 342 590',
    notes: 'Local police. For life-threatening emergencies dial 112.',
    tags: ['police', 'emergency'],
  },
  {
    id: 'bombeiros-lagoa',
    slug: 'bombeiros-lagoa',
    name: 'Bombeiros Voluntários de Lagoa',
    category: 'medical',
    locality: 'outside',
    address: 'Rua da Portela, 8400-397 Lagoa',
    lat: 37.1368,
    lng: -8.4486,
    phone: '+351 282 342 233',
    notes: 'Volunteer fire service. Ambulance via 112.',
    tags: ['fire', 'emergency'],
  },
  {
    id: 'lagoa-winery',
    slug: 'cooperativa-lagoa',
    name: 'Adega Cooperativa de Lagoa',
    category: 'attraction',
    locality: 'outside',
    address: 'EN125, 8400-427 Lagoa',
    lat: 37.1338,
    lng: -8.4588,
    notes: 'Co-op tasting room for Lagoa reds and moscatel.',
    tags: ['wine', 'lagoa'],
  },
];

export function placesWithDistance(): PlaceWithDistance[] {
  return PLACES.map((p) => {
    const distance_km = Number(haversineKm(HOME.lat, HOME.lng, p.lat, p.lng).toFixed(2));
    return {
      ...p,
      imageUrl: imageForPlace(p.id, p.category, p.slug) ?? p.imageUrl,
      distance_km,
      estimated_drive_minutes: estimateDriveMinutes(distance_km),
    };
  }).sort((a, b) => a.distance_km - b.distance_km);
}
